import _pick from 'lodash/pick';

import {
  FAILED_PATCH_USER_INFO,
  GET_USER_INFO,
  LOGOUT_USER,
  PASSWORD_VALIDATION,
  PATCHING_USER_INFO,
  SUCCESS_NOTIFICATION_SETTINGS,
  SUCCESS_PATCH_USER_INFO,
  SUCCESS_PATCH_USER_PASSWORD,
  UPDATE_CAPABILITY,
  UPDATE_DEFAULT_WORKSPACE,
  UPDATE_DELETION_PENDING,
  UPDATE_USER_ERROR,
  UPDATE_USER_INFO,
  UPDATE_USER_NOT_LOGGED_IN,
} from 'constants/actions';
import { CLIENT } from 'constants/userRoles';

const DEFAULT_USER_OBJECT = {
  avatars: [],
  basic_video_limit: null,
  capabilities: {},
  company_name: null,
  company_position: null,
  company_feature_data: null,
  deletion_pending: null,
  email: null,
  first_name: null,
  has_gmail_account: false,
  hasWebPushSubscription: true,
  id: null,
  is_primary: false,
  is_sdk_shared_user: false,
  last_name: null,
  num_credits: null,
  oauth: null,
  role: CLIENT,
  status: null,
  trial_status: null,
  password_is_set: null,
  invite_counts: null,
  onboarding: {},
  user_identity_id: null,
  account_type: null,
  has_activated_desktop_app: null,
  has_activated_chrome_extension: null,
  has_activated_ios_app: null,
  has_activated_android_app: null,
  workspaceType: null,
  default_workspace_id: null,
  tags: {},
  scopes: [],
  persona: {},
  createdAt: null,
  jtbd: {},
  video_settings: {},
};

const DEFAULT_USER_STATE = {
  current: DEFAULT_USER_OBJECT,
  error: null,
  is_fetching: false,
  is_logged_in: false,
  is_logged_in_checked: false,
  is_patching: false,
  success_patched: false, // show completion when updating user info in account settings
  success_patched_password: false, // show completion when updating user password in account settings
  valid_password: null, // used to indicate success/error in current password fields
  capabilities: [],
};

function _updateCurrentUser(state, user) {
  const newCurrentUser = _pick(user, Object.keys(state.current));

  newCurrentUser.displayName =
    `${newCurrentUser.first_name} ${newCurrentUser.last_name}`
      .replace(/null/g, '')
      .trim();

  return Object.assign({}, state.current, newCurrentUser);
}

function getCapabilitiesFromUser(user) {
  if (user == null || user.capabilities == null) {
    return [];
  }

  return Object.entries(user.capabilities).reduce((arr, entry) => {
    return entry[1] && arr.push(entry[0]), arr;
  }, []);
}

const userReducer = (state = DEFAULT_USER_STATE, action) => {
  switch (action.type) {
    case GET_USER_INFO:
      return {
        ...state,
        error: null,
        is_fetching: true,
      };
    case LOGOUT_USER:
      return DEFAULT_USER_STATE;
    case UPDATE_USER_ERROR:
      return {
        ...state,
        error: action.err,
        is_fetching: false,
      };
    case UPDATE_USER_INFO:
      return {
        ...state,
        current: _updateCurrentUser(state, action.user),
        error: null,
        is_fetching: false,
        is_logged_in: true,
        is_logged_in_checked: true,
        capabilities: getCapabilitiesFromUser(action.user),
      };
    case UPDATE_USER_NOT_LOGGED_IN:
      return {
        ...state,
        current: _updateCurrentUser(state, action.user),
        error: null,
        is_fetching: false,
        is_logged_in: false,
        is_logged_in_checked: true,
        capabilities: getCapabilitiesFromUser(action.user),
      };

    case PATCHING_USER_INFO:
      return {
        ...state,
        is_patching: true,
        success_patched: false,
        success_patched_password: false,
      };
    case SUCCESS_PATCH_USER_INFO:
      return {
        ...state,
        is_patching: false,
        success_patched: true,
      };
    case FAILED_PATCH_USER_INFO:
      return {
        ...state,
        is_patching: false,
        success_patched: false,
        success_patched_password: false,
      };
    case SUCCESS_PATCH_USER_PASSWORD:
      return {
        ...state,
        is_patching: false,
        success_patched_password: true,
      };
    case PASSWORD_VALIDATION:
      return {
        ...state,
        valid_password: action.valid,
      };
    case UPDATE_CAPABILITY:
      return {
        ...state,
        capabilities: action.isCapable
          ? state.capabilities.concat([action.capability])
          : state.capabilities.filter(cap => cap != action.capability),
      };
    case UPDATE_DEFAULT_WORKSPACE:
      return {
        ...state,
        current: {
          ...state.current,
          default_workspace_id: action.workspaceId,
        },
      };
    case UPDATE_DELETION_PENDING:
      return {
        ...state,
        current: {
          ...state.current,
          deletion_pending: action.deletionPending,
        },
      };

    case SUCCESS_NOTIFICATION_SETTINGS:
      return {
        ...state,
        current: {
          ...state.current,
          hasWebPushSubscription: true,
        },
      };
    default:
      return state;
  }
};

export default userReducer;
